import React, { useState } from 'react';
import Papa from 'papaparse';
import { bulkImportClients } from '../utils/api';
import { ar, en } from '../utils/translations';

// Simple vCard parser
const parseVCard = (text) => {
  const contacts = [];
  // Normalize line endings and split by BEGIN:VCARD
  const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const vcards = normalizedText.split('BEGIN:VCARD');
  
  vcards.forEach(vcard => {
    if (!vcard.trim() || !vcard.includes('END:VCARD')) return;
    
    // Handle multi-line fields (vCard uses space+tab for continuation)
    const processedVCard = vcard.replace(/\n[ \t]/g, '');
    const lines = processedVCard.split('\n');
    
    let name = '';
    let phone = '';
    let phones = []; // Collect all phones, prefer CELL/MOBILE
    let email = '';
    let address = '';
    
    lines.forEach(line => {
      line = line.trim();
      if (!line) return;
      
      // Parse FN (Full Name) - preferred
      if (line.startsWith('FN:')) {
        name = line.substring(3).trim();
      }
      // Parse N (Name components) - fallback
      else if (line.startsWith('N:') && !name) {
        const nameParts = line.substring(2).split(';');
        const lastName = nameParts[0] || '';
        const firstName = nameParts[1] || '';
        name = `${firstName} ${lastName}`.trim() || nameParts[0] || '';
      }
      // Parse TEL (Phone) - collect all, prefer CELL/MOBILE/IPHONE
      else if (line.startsWith('TEL')) {
        const phoneMatch = line.match(/TEL[^:]*:(.+)/);
        if (phoneMatch) {
          const phoneNum = phoneMatch[1].trim();
          const lineUpper = line.toUpperCase();
          // Prefer mobile/cell/iphone numbers
          if (lineUpper.includes('CELL') || lineUpper.includes('MOBILE') || lineUpper.includes('IPHONE')) {
            if (!phone) phone = phoneNum;
            phones.unshift(phoneNum); // Add to front
          } else {
            phones.push(phoneNum);
          }
        }
      }
      // Parse EMAIL
      else if (line.startsWith('EMAIL')) {
        const emailMatch = line.match(/EMAIL[^:]*:(.+)/);
        if (emailMatch && !email) {
          email = emailMatch[1].trim();
        }
      }
      // Parse ADR (Address)
      else if (line.startsWith('ADR')) {
        const addrMatch = line.match(/ADR[^:]*:(.+)/);
        if (addrMatch && !address) {
          const addrParts = addrMatch[1].split(';');
          address = addrParts.filter(part => part.trim()).join(', ').trim();
        }
      }
    });
    
    // Use first phone if we don't have a preferred one
    if (!phone && phones.length > 0) {
      phone = phones[0];
    }
    
    if (name) {
      contacts.push({
        name: name,
        phone: phone || '',
        email: email || '',
        whatsapp: phone || '',
        address: address || '',
        notes: phones.length > 1 ? `Other phones: ${phones.slice(1).join(', ')}` : '',
        source: 'Imported from iPhone'
      });
    }
  });
  
  return contacts;
};

const ImportContacts = ({ onClose, onImportComplete, language = 'ar' }) => {
  const t = language === 'ar' ? ar : en;
  // All imported contacts default to 'customer' - users can change it manually later
  const importType = 'customer';
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [columnNames, setColumnNames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [activeTab, setActiveTab] = useState('iphone'); // 'iphone' or 'android'

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
    if (fileExtension !== 'csv' && fileExtension !== 'vcf') {
      alert(t.pleaseSelectCSV);
      return;
    }

    setFile(selectedFile);
    setResult(null);

    // Read file content
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      
      if (fileExtension === 'vcf') {
        // Parse vCard file
        const contacts = parseVCard(text);
        setColumnNames(['Name', 'Phone', 'Email', 'Address']);
        setPreview(contacts.slice(0, 10));
        console.log('Parsed vCard contacts:', contacts.length);
      } else {
        // Parse CSV file
        Papa.parse(selectedFile, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            const firstRow = results.data[0] || {};
            const cols = Object.keys(firstRow);
            setColumnNames(cols);
            setPreview(results.data.slice(0, 10)); // Show first 10 rows
            console.log('Detected columns:', cols);
          },
          error: (error) => {
            alert(t.errorReadingFile + ': ' + error.message);
          }
        });
      }
    };
    reader.readAsText(selectedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleFileInput = (e) => {
    handleFileSelect(e.target.files[0]);
  };

  // Import contacts in batches to avoid payload size limits
  const importInBatches = async (contacts, customerType) => {
    const BATCH_SIZE = 100; // Process 100 contacts at a time
    const totalBatches = Math.ceil(contacts.length / BATCH_SIZE);
    let totalSuccess = 0;
    let totalErrors = 0;
    const allErrors = [];

    setImportProgress({ current: 0, total: totalBatches, processed: 0, totalContacts: contacts.length });

    for (let i = 0; i < contacts.length; i += BATCH_SIZE) {
      const batch = contacts.slice(i, i + BATCH_SIZE);
      const batchNumber = Math.floor(i / BATCH_SIZE) + 1;

      try {
        // Add customer_type to each contact in the batch
        const batchWithType = batch.map(contact => ({
          ...contact,
          customer_type: customerType
        }));
        
        const response = await bulkImportClients(batchWithType);

        totalSuccess += response.data.successCount || 0;
        
        // Update progress after successful batch
        setImportProgress({
          current: batchNumber,
          total: totalBatches,
          processed: Math.min(i + batch.length, contacts.length),
          totalContacts: contacts.length
        });
        if (response.data.errors && response.data.errors.length > 0) {
          totalErrors += response.data.errorCount || 0;
          allErrors.push(...response.data.errors);
        }

        // Small delay between batches to avoid overwhelming the server
        if (i + BATCH_SIZE < contacts.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        console.error(`Error importing batch ${batchNumber}:`, error);
        totalErrors += batch.length;
        allErrors.push(`Batch ${batchNumber}: ${error.response?.data?.error || error.message}`);
      }
    }

    setImportProgress(null);
    setResult({
      successCount: totalSuccess,
      errorCount: totalErrors,
      errors: allErrors.length > 0 ? allErrors : undefined
    });

    if (totalSuccess > 0) {
      setTimeout(() => {
        onImportComplete();
      }, 2000);
    }

    setLoading(false);
  };

  const handleImport = async () => {
    if (!file) {
      alert(t.pleaseSelectFile);
      return;
    }

    setLoading(true);
    setResult(null);

    const fileExtension = file.name.split('.').pop().toLowerCase();
    
    // Handle vCard files
    if (fileExtension === 'vcf') {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target.result;
          const contacts = parseVCard(text);
          
          if (contacts.length === 0) {
            alert(t.noValidContactsVCF);
            setLoading(false);
            setImportProgress(null);
            return;
          }
          
          // Process in batches to avoid payload size issues
          await importInBatches(contacts, importType);
          return;
        } catch (error) {
          console.error('Import error:', error);
          alert(t.failedToImport + ': ' + (error.response?.data?.error || error.message));
          setLoading(false);
          setImportProgress(null);
        }
      };
      reader.readAsText(file);
      return;
    }

    // Handle CSV files
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          // Get all column names from the first row for debugging
          const firstRow = results.data[0] || {};
          const columnNames = Object.keys(firstRow);
          console.log('CSV columns found:', columnNames);
          
          const contacts = results.data.map(row => {
            // Try to find name field - handle many variations including First/Last Name
            let name = '';
            const nameVariations = [
              'name', 'Name', 'NAME', 'Full Name', 'Full name', 'full name',
              'Display Name', 'display name', 'DisplayName',
              'Given Name', 'given name', 'GivenName',
              'First Name', 'first name', 'FirstName', 'First',
              'Last Name', 'last name', 'LastName', 'Last',
              'Contact', 'contact', 'Title', 'title'
            ];
            
            // First try to find a combined name field
            for (const key of nameVariations) {
              if (row[key] && row[key].trim()) {
                name = row[key].trim();
                break;
              }
            }
            
            // If no combined name, try First Name + Last Name
            if (!name) {
              const firstName = row['First Name'] || row['first name'] || row['FirstName'] || row['First'] || row['Given Name'] || row['GivenName'] || '';
              const lastName = row['Last Name'] || row['last name'] || row['LastName'] || row['Last'] || row['Family Name'] || row['FamilyName'] || '';
              if (firstName || lastName) {
                name = `${firstName} ${lastName}`.trim();
              }
            }
            
            // Try to find phone - handle many variations
            const phoneVariations = [
              'phone', 'Phone', 'PHONE', 'Phone Number', 'phone number', 'PhoneNumber',
              'mobile', 'Mobile', 'MOBILE', 'Mobile Number', 'mobile number',
              'Cell', 'cell', 'Cell Phone', 'cell phone', 'CellPhone',
              'Telephone', 'telephone', 'Tel', 'tel',
              'Phone 1', 'Phone 2', 'Phone 3', 'Phone 4',
              'Phone - Mobile', 'Phone - Work', 'Phone - Home'
            ];
            
            let phone = '';
            for (const key of phoneVariations) {
              if (row[key] && row[key].trim()) {
                phone = row[key].trim();
                break;
              }
            }
            
            // Try to find email
            const emailVariations = [
              'email', 'Email', 'EMAIL', 'E-mail', 'e-mail', 'E-Mail',
              'Email Address', 'email address', 'EmailAddress',
              'Email 1', 'Email 2', 'Email 3'
            ];
            
            let email = '';
            for (const key of emailVariations) {
              if (row[key] && row[key].trim()) {
                email = row[key].trim();
                break;
              }
            }
            
            // Try to find address
            const addressVariations = [
              'address', 'Address', 'ADDRESS',
              'Street', 'street', 'Street Address', 'street address',
              'Home Address', 'home address', 'Work Address', 'work address',
              'Address 1', 'Address 2'
            ];
            
            let address = '';
            for (const key of addressVariations) {
              if (row[key] && row[key].trim()) {
                address = row[key].trim();
                break;
              }
            }
            
            // Try to find notes/comment
            const notesVariations = [
              'notes', 'Notes', 'NOTES', 'Note', 'note',
              'comment', 'Comment', 'COMMENT', 'Comments', 'comments',
              'Memo', 'memo', 'Description', 'description'
            ];
            
            let notes = '';
            for (const key of notesVariations) {
              if (row[key] && row[key].trim()) {
                notes = row[key].trim();
                break;
              }
            }
            
            return {
              name: name,
              phone: phone,
              email: email,
              whatsapp: phone || '', // Use phone as WhatsApp if no separate WhatsApp field
              address: address,
              notes: notes,
              source: row.source || row.Source || row.SOURCE || 'Imported'
            };
          }).filter(contact => contact.name.trim() !== ''); // Remove empty rows

          if (contacts.length === 0) {
            const columnList = columnNames.length > 0 
              ? `\n\n${language === 'ar' ? 'الأعمدة الموجودة في ملفك:' : 'Found columns in your file:'} ${columnNames.join(', ')}\n\n${language === 'ar' ? 'تأكد من أن ملف CSV يحتوي على عمود بأسماء جهات الاتصال (مثل "Name", "First Name", "Full Name", إلخ)' : 'Please make sure your CSV has a column with contact names (like "Name", "First Name", "Full Name", etc.)'}`
              : `\n\n${language === 'ar' ? 'لم يتم اكتشاف أي أعمدة في ملفك.' : 'Could not detect any columns in your file.'}`;
            alert((language === 'ar' ? 'لم يتم العثور على جهات اتصال صالحة في الملف.' : 'No valid contacts found in the file.') + columnList);
            setLoading(false);
            setImportProgress(null);
            return;
          }

          // Process in batches to avoid payload size issues
          await importInBatches(contacts, importType);
        } catch (error) {
          console.error('Import error:', error);
          alert(t.failedToImport + ': ' + (error.response?.data?.error || error.message));
          setLoading(false);
          setImportProgress(null);
        }
      },
      error: (error) => {
        alert(t.errorReadingFile + ': ' + error.message);
        setLoading(false);
      }
    });
  };

  return (
      <div className="card">
      <div className="modal-header" style={{ marginBottom: '20px' }}>
        <h2>📥 {t.importTitle}</h2>
        <button
          className="btn btn-secondary"
          onClick={onClose}
          style={{ padding: '8px 14px' }}
        >
          {t.cancel}
        </button>
      </div>

      <div className="import-section">
        <div className="import-instructions">
          <h3>📱 {t.howToImport}</h3>
          <div style={{ marginBottom: '20px', padding: '15px', background: '#fff4e6', borderRadius: '8px', border: '1px solid #ffd4a3' }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: '600', color: '#000000' }}>
              {language === 'ar' ? '💡 نصيحة سريعة:' : '💡 Quick Tip:'}
            </p>
            <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.6', color: '#000000' }}>
              {language === 'ar' 
                ? 'أسهل طريقة: استخدم ميزة "تصدير جهات الاتصال" من تطبيق جهات الاتصال في هاتفك مباشرة.'
                : 'Easiest way: Use the "Export Contacts" feature directly from your phone\'s Contacts app.'}
            </p>
          </div>
          
          {/* Device Tabs */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
            <button
              onClick={() => setActiveTab('iphone')}
              style={{
                padding: '12px 24px',
                background: activeTab === 'iphone' ? '#ff9500' : '#f5f5f5',
                color: activeTab === 'iphone' ? '#ffffff' : '#000000',
                border: 'none',
                borderBottom: activeTab === 'iphone' ? '3px solid #ff9500' : '3px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '1rem',
                transition: 'all 0.2s'
              }}
            >
              📱 {language === 'ar' ? 'iPhone' : 'iPhone'}
            </button>
            <button
              onClick={() => setActiveTab('android')}
              style={{
                padding: '12px 24px',
                background: activeTab === 'android' ? '#ff9500' : '#f5f5f5',
                color: activeTab === 'android' ? '#ffffff' : '#000000',
                border: 'none',
                borderBottom: activeTab === 'android' ? '3px solid #ff9500' : '3px solid transparent',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '1rem',
                transition: 'all 0.2s'
              }}
            >
              🤖 {language === 'ar' ? 'Android' : 'Android'}
            </button>
          </div>

          {/* iPhone Instructions */}
          {activeTab === 'iphone' && (
            <div style={{ padding: '15px', background: '#fafafa', borderRadius: '8px' }}>
              <ol style={{ marginTop: '10px', marginLeft: '20px', listStyleType: 'decimal' }}>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' ? 'افتح تطبيق "جهات الاتصال" (Contacts)' : 'Go to the Contacts app on your iPhone'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' ? 'اضغط على زر "Lists" في الأعلى يساراً' : 'Tap the Lists button at the top left'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' 
                    ? 'اضغط مطولاً على قائمة موجودة (أو أنشئ قائمة جديدة بالضغط على Add List)'
                    : 'Touch and hold an existing list (or create a new list by tapping Add List)'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' 
                    ? 'اضغط على "Export"، اختر الحقول التي تريد تضمينها، ثم اضغط "Done"'
                    : 'Tap Export, select the fields you want to include, then tap the Done button'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' 
                    ? 'اختر طريقة لإرسال أو حفظ بطاقات الاتصال المُصدّرة (مثل Messages أو Mail)'
                    : 'Choose a method to send or save the exported contact cards, such as Messages or Mail'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6', color: '#ff9500', fontWeight: '600' }}>
                  {language === 'ar' 
                    ? '✅ ارفع الملف المُصدّر أدناه'
                    : '✅ Upload the exported file below'}
                </li>
              </ol>
            </div>
          )}

          {/* Android Instructions */}
          {activeTab === 'android' && (
            <div style={{ padding: '15px', background: '#fafafa', borderRadius: '8px' }}>
              <ol style={{ marginTop: '10px', marginLeft: '20px', listStyleType: 'decimal' }}>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' ? 'افتح تطبيق "جهات الاتصال" (Contacts)' : 'Open the "Contacts" app'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' 
                    ? 'ابحث عن "Fix & manage" أو "Organize" أو اضغط على القائمة (☰) → "Settings"'
                    : 'Look for "Fix & manage", "Organize", or tap the menu (☰) → "Settings"'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6' }}>
                  {language === 'ar' 
                    ? 'اضغط "Export to file" أو "Export"'
                    : 'Tap "Export to file" or "Export"'}
                </li>
                <li style={{ marginBottom: '8px', lineHeight: '1.6', color: '#ff9500', fontWeight: '600' }}>
                  {language === 'ar' 
                    ? '✅ احفظ الملف ثم ارفعه أدناه'
                    : '✅ Save the file, then upload it below'}
                </li>
              </ol>
            </div>
          )}

        </div>

        <div
          className={`file-upload-area ${isDragging ? 'dragover' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('file-input').click()}
        >
          <input
            id="file-input"
            type="file"
            accept=".csv,.vcf"
            onChange={handleFileInput}
            style={{ display: 'none' }}
          />
          <div className="upload-icon">📄</div>
          <h3 style={{ marginBottom: '8px' }}>
            {language === 'ar' ? 'أسقط ملفك هنا أو انقر للتصفح' : 'Drop your file here or click to browse'}
          </h3>
          <p style={{ color: '#000000', marginTop: '8px', fontSize: '0.9rem', opacity: '0.7' }}>
            {language === 'ar' ? 'الصيغ المدعومة: CSV أو vcf' : 'Supported formats: CSV or vcf'}
          </p>
          {file && (
            <p style={{ marginTop: '15px', color: '#000000', fontWeight: '500' }}>
              Selected: {file.name}
            </p>
          )}
        </div>

        {importProgress && (
          <div style={{
            background: '#ffe8cc',
            border: '2px solid #ffb84d',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginBottom: '15px', color: '#000000' }}>📊 {t.importing}...</h3>
            <div style={{ marginBottom: '10px' }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '8px',
                height: '30px',
                overflow: 'hidden',
                border: '1px solid #ffb84d'
              }}>
                <div style={{
                  background: '#ffb84d',
                  height: '100%',
                  width: `${(importProgress.processed / importProgress.totalContacts) * 100}%`,
                  transition: 'width 0.3s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#000000',
                  fontWeight: '500',
                  fontSize: '0.9rem'
                }}>
                  {Math.round((importProgress.processed / importProgress.totalContacts) * 100)}%
                </div>
              </div>
            </div>
            <p style={{ color: '#000000', margin: 0, fontSize: '0.9rem' }}>
              Processing batch {importProgress.current} of {importProgress.total} 
              ({importProgress.processed} of {importProgress.totalContacts} contacts)
            </p>
          </div>
        )}

        {preview.length > 0 && (
          <div className="import-preview">
            <h3 style={{ marginBottom: '15px' }}>{t.preview}</h3>
            <table className="preview-table">
              <thead>
                <tr>
                  {Object.keys(preview[0] || {}).map(key => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.map((row, idx) => (
                  <tr key={idx}>
                    {Object.values(row).map((value, i) => (
                      <td key={i}>{String(value || '').substring(0, 50)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ padding: '15px', color: '#000000', fontSize: '0.9rem', opacity: '0.7' }}>
              {t.totalRows}
            </p>
          </div>
        )}

        {result && (
          <div className={result.errorCount > 0 ? 'error-message' : 'success-message'}>
            <h3>{result.errorCount > 0 ? `⚠️ ${t.importCompletedWithErrors}` : `✅ ${t.successfullyImported}`}</h3>
            <p>{t.successfullyImportedCount}: {result.successCount} {t.contacts}</p>
            {result.errorCount > 0 && (
              <div>
                <p>{t.errors}: {result.errorCount}</p>
                {result.errors && result.errors.length > 0 && (
                  <ul style={{ marginTop: '10px', marginLeft: '20px' }}>
                    {result.errors.slice(0, 5).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {result.errors.length > 5 && <li>... and {result.errors.length - 5} more</li>}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={!file || loading}
          >
            {loading ? t.importing : (language === 'ar' ? 'استيراد جهات الاتصال' : 'Import Contacts')}
          </button>
          <button className="btn btn-secondary" onClick={onClose}>
            {t.cancel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportContacts;

